import { BaseModal } from "@/components/modal/base-modal";
import { FilledButton } from "@/components/ui/buttons";
import { useViewportHeight } from "@/hooks/utils/use-viewport-height";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo } from "react";

interface ProfileStatusDetailsModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  type: "joined" | "started" | "current" | null;
  joinedDate?: Date;
  joinedDays?: number;
  startedWeight?: number;
  startedWeightUnit?: string;
  currentWeight?: number;
  currentWeightUnit?: string;
}

interface DetailRowProps {
  label: string;
  value: string;
}

const DetailRow = memo(function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div className="flex items-center justify-between px-4 py-4">
      <span className={cn(textVariants.inputText, "font-normal")}>{label}</span>
      <span className={cn(textVariants.inputText, "font-normal")}>{value}</span>
    </div>
  );
});

export const ProfileStatusDetailsModal = memo(function ProfileStatusDetailsModal({
  isOpen,
  onDismiss,
  type,
  joinedDate,
  joinedDays,
  startedWeight,
  startedWeightUnit,
  currentWeight,
  currentWeightUnit,
}: ProfileStatusDetailsModalProps) {
  const viewportHeight = useViewportHeight();
  const getModalContent = () => {
    switch (type) {
      case "joined": {
        // Calculate days this week (since Monday)
        const now = new Date();
        const dayOfWeek = now.getDay();
        const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const mondayThisWeek = new Date(now);
        mondayThisWeek.setDate(now.getDate() - mondayOffset);
        mondayThisWeek.setHours(0, 0, 0, 0);

        const daysThisWeek =
          joinedDate && joinedDate > mondayThisWeek
            ? Math.floor((now.getTime() - joinedDate.getTime()) / (1000 * 60 * 60 * 24))
            : Math.floor((now.getTime() - mondayThisWeek.getTime()) / (1000 * 60 * 60 * 24));

        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        firstDayOfMonth.setHours(0, 0, 0, 0);

        const daysThisMonth =
          joinedDate && joinedDate > firstDayOfMonth
            ? Math.floor((now.getTime() - joinedDate.getTime()) / (1000 * 60 * 60 * 24))
            : Math.floor((now.getTime() - firstDayOfMonth.getTime()) / (1000 * 60 * 60 * 24));

        const weeksOnPlan = Math.floor((joinedDays ?? 0) / 7);

        return {
          title: "On Plan",
          subtitle: "Remember, progress is incremental.",
          sections: [
            {
              label: "Days this week",
              value: `${daysThisWeek}`,
            },
            {
              label: "Days this month",
              value: `${daysThisMonth}`,
            },
            {
              label: "Weeks on plan",
              value: `${weeksOnPlan}`,
            },
          ],
        };
      }
      case "started":
        return {
          title: "Starting Weight",
          subtitle: "Your journey begins here.",
          sections: [
            {
              label: "Starting weight",
              value: `${startedWeight ?? 0} ${startedWeightUnit ?? "kg"}`,
            },
          ],
        };
      case "current":
        return {
          title: "Current Weight",
          subtitle: "Track your progress over time.",
          sections: [
            {
              label: "Current weight",
              value: `${currentWeight ?? 0} ${currentWeightUnit ?? "kg"}`,
            },
          ],
        };
      default:
        return null;
    }
  };

  const content = getModalContent();

  if (!content) return null;

  return (
    <BaseModal
      isOpen={isOpen}
      onDismiss={onDismiss}
      backdropDismiss={true}
      initialBreakpoint={393 / viewportHeight}
      breakpoints={[0, 393 / viewportHeight, 0.75]}
    >
      <div className="safe-area-bottom flex flex-col gap-6 bg-white px-8 pt-8 pb-6">
        <div className="flex flex-col items-center">
          <h2 className={cn(textVariants.title3, "h-7.5")}>{content.title}</h2>
          <p className={cn(textVariants.footnote, "h-7.5")}>{content.subtitle}</p>
        </div>

        <div className="flex flex-col gap-3 px-2">
          {content.sections.map((section, index) => (
            <DetailRow key={index} label={section.label} value={section.value} />
          ))}
        </div>

        <FilledButton color="purple" onClick={onDismiss}>
          Done
        </FilledButton>
      </div>
    </BaseModal>
  );
});
